import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CARD_PALETTE, type CardAccent } from "./snapshot.js";

interface CardDialogProps {
  title: string;
  initialTitle: string;
  initialBody: string;
  initialAccent: CardAccent;
  onConfirm: (next: { title: string; body: string; accent: CardAccent }) => void;
  onClose: () => void;
}

/**
 * Editor for whiteboard "Karte" (Übungsblock) widgets — title + body + accent.
 * Renders into document.body via portal and shares look & feel with the
 * generic `LabelDialog` used elsewhere in the editor.
 */
export function CardDialog({
  title,
  initialTitle,
  initialBody,
  initialAccent,
  onConfirm,
  onClose,
}: CardDialogProps) {
  const [titleValue, setTitleValue] = useState(initialTitle);
  const [body, setBody] = useState(initialBody);
  const [accent, setAccent] = useState<CardAccent>(initialAccent);
  const titleRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    titleRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = () => onConfirm({ title: titleValue.trim(), body: body.trim(), accent });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="glass-floating w-[min(420px,calc(100vw-32px))] rounded-3xl p-4 shadow-2xl">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-body-sm font-semibold uppercase tracking-wider">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Schließen"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-white/6 hover:bg-white/10"
          >
            <X className="size-3.5" />
          </button>
        </header>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          className="flex flex-col gap-3"
        >
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Titel
            </span>
            <input
              ref={titleRef}
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              maxLength={60}
              className="h-10 rounded-2xl border border-white/15 bg-white/4 px-3 text-body-sm outline-none focus:border-pitch-lime/60 focus:bg-white/7"
              placeholder="z.B. Passspiel 4-gegen-2"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Beschreibung
            </span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={5}
              maxLength={600}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  submit();
                }
              }}
              className="min-h-30 resize-y rounded-2xl border border-white/15 bg-white/4 px-3 py-2 text-body-sm leading-snug outline-none focus:border-pitch-lime/60 focus:bg-white/7"
              placeholder="Ablauf, Ziel, Coaching-Punkte …"
            />
          </label>

          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Farbakzent
            </span>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(CARD_PALETTE) as CardAccent[]).map((id) => {
                const p = CARD_PALETTE[id];
                const active = accent === id;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setAccent(id)}
                    aria-pressed={active}
                    className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-body-sm transition ${
                      active
                        ? "border-pitch-lime bg-white/10"
                        : "border-white/15 bg-white/4 hover:bg-white/10"
                    }`}
                  >
                    <span className="size-3.5 rounded-full" style={{ background: p.stroke }} />
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-full border border-white/15 bg-white/4 px-4 text-body-sm hover:bg-white/10"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="h-10 rounded-full bg-pitch-lime px-4 text-body-sm font-semibold text-[#1d3700] shadow-glow hover:brightness-110"
            >
              Übernehmen
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
